import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Download,
  Loader2,
  LogOut,
  Mail,
  RefreshCw,
  Search,
  Trash2,
  Inbox,
  Package,
  Users,
  Target,
  Handshake,
  Wallet,
  Briefcase,
  IdCard,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import AdminOrders from "@/pages/AdminOrders";
import AdminVendors from "@/pages/AdminVendors";
import AdminApplications from "@/pages/AdminApplications";
import AdminPayouts from "@/pages/AdminPayouts";
import AdminCareers from "@/pages/AdminCareers";
import AdminEmployees from "@/pages/AdminEmployees";
import LeadsPanel from "@/components/LeadsPanel";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sriman-enterprise/artifacts/ng31c679_image.png";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const { user, logout, authHeader, apiBase, token } = useAuth();
  const [tab, setTab] = useState("inquiries"); // 'inquiries' | 'orders' | 'leads' | 'vendors'
  const [vendors, setVendors] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Preload vendors for the Leads tab lookup
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/admin/vendors`, {
          headers: authHeader(),
        });
        setVendors(data);
      } catch {
        /* ignore */
      }
    })();
  }, [apiBase, authHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/inquiries`, {
        headers: authHeader(),
      });
      setInquiries(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load inquiries",
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return inquiries;
    return inquiries.filter((i) =>
      [i.name, i.email, i.company, i.service, i.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [inquiries, query]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${apiBase}/admin/inquiries/${id}`, {
        headers: authHeader(),
      });
      setInquiries((list) => list.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Inquiry deleted.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Delete failed",
      );
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${apiBase}/admin/inquiries/export.csv`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `sriman-inquiries-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV export downloaded.");
    } catch (err) {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const stats = useMemo(() => {
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const last7 = inquiries.filter(
      (i) => now - new Date(i.created_at).getTime() < 7 * dayMs,
    ).length;
    const last30 = inquiries.filter(
      (i) => now - new Date(i.created_at).getTime() < 30 * dayMs,
    ).length;
    return { total: inquiries.length, last7, last30 };
  }, [inquiries]);

  return (
    <main
      data-testid="admin-dashboard-page"
      className="min-h-screen bg-[#040812] pt-24 pb-24"
    >
      {/* Header */}
      <div className="border-b border-white/5 bg-[#040812]/95 backdrop-blur-xl fixed top-0 inset-x-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="h-9 w-9">
              <img
                src={LOGO_URL}
                alt="Sriman Solutions"
                className="h-full w-full object-contain"
              />
            </span>
            <div className="flex flex-col leading-none">
              <span className="font-display text-white text-sm tracking-tight">
                Sriman Solutions
              </span>
              <span className="font-mono text-[9px] text-red-500 tracking-[0.28em] uppercase mt-1">
                Admin Console
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:block text-xs text-slate-500 font-mono">
              {user?.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-white text-xs font-medium px-4 py-2 rounded-sm transition-colors"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-6">
        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-1 bg-[#0B132B]/60 border border-white/10 p-1 rounded-sm w-fit mb-8">
          <button
            onClick={() => setTab("inquiries")}
            data-testid="admin-tab-inquiries"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
              tab === "inquiries"
                ? "bg-red-500 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Inbox size={14} />
            Inquiries
          </button>
          <button
            onClick={() => setTab("orders")}
            data-testid="admin-tab-orders"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
              tab === "orders"
                ? "bg-red-500 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Package size={14} />
            Orders
          </button>
          <button
            onClick={() => setTab("leads")}
            data-testid="admin-tab-leads"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
              tab === "leads"
                ? "bg-red-500 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Target size={14} />
            Leads
          </button>
          <button
            onClick={() => setTab("vendors")}
            data-testid="admin-tab-vendors"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
              tab === "vendors"
                ? "bg-red-500 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Users size={14} />
            Vendors
          </button>
          <button
            onClick={() => setTab("applications")}
            data-testid="admin-tab-applications"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
              tab === "applications"
                ? "bg-red-500 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Handshake size={14} />
            Applications
          </button>
          <button
            onClick={() => setTab("payouts")}
            data-testid="admin-tab-payouts"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
              tab === "payouts"
                ? "bg-red-500 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Wallet size={14} />
            Payouts
          </button>
          <button
            onClick={() => setTab("careers")}
            data-testid="admin-tab-careers"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
              tab === "careers"
                ? "bg-red-500 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Briefcase size={14} />
            Careers
          </button>
          <button
            onClick={() => setTab("employees")}
            data-testid="admin-tab-employees"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
              tab === "employees"
                ? "bg-red-500 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <IdCard size={14} />
            Employees
          </button>
        </div>

        {tab === "orders" ? (
          <AdminOrders />
        ) : tab === "leads" ? (
          <LeadsPanel variant="admin" vendors={vendors} />
        ) : tab === "vendors" ? (
          <AdminVendors onVendorsChanged={setVendors} />
        ) : tab === "applications" ? (
          <AdminApplications />
        ) : tab === "payouts" ? (
          <AdminPayouts />
        ) : tab === "careers" ? (
          <AdminCareers />
        ) : tab === "employees" ? (
          <AdminEmployees />
        ) : (
          <>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="overline">/Admin · Inquiries</span>
            <h1 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
              Client inquiries
            </h1>
            <p className="mt-3 text-slate-500 text-sm">
              Review, triage and export submitted consultation requests.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              disabled={loading}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || inquiries.length === 0}
              data-testid="admin-export-csv-button"
              className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors"
            >
              {exporting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { k: stats.total, v: "Total inquiries", id: "stat-total" },
            { k: stats.last7, v: "Last 7 days", id: "stat-7d" },
            { k: stats.last30, v: "Last 30 days", id: "stat-30d" },
          ].map((s) => (
            <div
              key={s.v}
              data-testid={s.id}
              className="bg-[#0B132B]/50 border border-white/10 p-6"
            >
              <div className="font-display text-white text-3xl tracking-tight">
                {s.k}
              </div>
              <div className="mt-2 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="mt-8 relative">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <input
            data-testid="admin-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, company, service or message…"
            className="w-full bg-[#0B132B]/60 border border-white/10 focus:border-red-500/60 focus:outline-none text-white placeholder-slate-500 pl-11 pr-4 py-3 text-sm rounded-sm transition-colors"
          />
        </div>

        {/* Table */}
        <div className="mt-6 bg-[#0B132B]/40 border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-inquiries-table">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                    Received
                  </th>
                  <th className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                    Name
                  </th>
                  <th className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                    Email
                  </th>
                  <th className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                    Company
                  </th>
                  <th className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                    Service
                  </th>
                  <th className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <Loader2
                        size={20}
                        className="inline animate-spin text-slate-500"
                      />
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <div className="text-slate-500 text-sm">
                        {inquiries.length === 0
                          ? "No inquiries yet. Submissions from the website will appear here."
                          : "No inquiries match your search."}
                      </div>
                    </td>
                  </tr>
                )}
                {!loading &&
                  filtered.map((row) => (
                    <tr
                      key={row.id}
                      data-testid={`admin-inquiry-row-${row.id}`}
                      onClick={() => setSelected(row)}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 text-slate-400 font-mono text-xs whitespace-nowrap">
                        {formatDate(row.created_at)}
                      </td>
                      <td className="px-6 py-4 text-white">{row.name}</td>
                      <td className="px-6 py-4 text-slate-300">
                        <a
                          href={`mailto:${row.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-red-400 inline-flex items-center gap-1"
                        >
                          <Mail size={12} />
                          {row.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-slate-400">
                        {row.company || "—"}
                      </td>
                      <td className="px-6 py-4 text-slate-400">
                        {row.service || "—"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(row.id);
                          }}
                          data-testid={`admin-inquiry-delete-${row.id}`}
                          className="inline-flex items-center gap-1 text-slate-500 hover:text-red-500 text-xs transition-colors"
                        >
                          <Trash2 size={13} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="admin-inquiry-drawer"
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg bg-[#0B132B] border-l border-white/10 h-full overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Inquiry detail</div>
                <h2 className="mt-3 font-display text-white text-2xl tracking-tight">
                  {selected.name}
                </h2>
                <div className="mt-1 text-slate-400 text-sm">
                  {selected.company || "—"}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="admin-drawer-close"
                className="text-slate-400 hover:text-white text-sm"
              >
                Close
              </button>
            </div>

            <dl className="mt-8 space-y-5 text-sm">
              {[
                { k: "Received", v: formatDate(selected.created_at) },
                {
                  k: "Email",
                  v: (
                    <a
                      href={`mailto:${selected.email}`}
                      className="text-red-400 hover:underline"
                    >
                      {selected.email}
                    </a>
                  ),
                },
                { k: "Phone", v: selected.phone || "—" },
                { k: "Service of interest", v: selected.service || "—" },
              ].map((f) => (
                <div key={f.k}>
                  <dt className="overline mb-1">{f.k}</dt>
                  <dd className="text-white">{f.v}</dd>
                </div>
              ))}
              <div>
                <dt className="overline mb-1">Message</dt>
                <dd className="text-slate-200 whitespace-pre-wrap leading-relaxed border-l-2 border-red-500/40 pl-4">
                  {selected.message}
                </dd>
              </div>
            </dl>

            <div className="mt-10 flex items-center gap-3">
              <a
                href={`mailto:${selected.email}?subject=Re: Your inquiry to Sriman Solutions`}
                className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-5 py-3 rounded-sm"
              >
                <Mail size={14} />
                Reply via email
              </a>
              <button
                onClick={() => handleDelete(selected.id)}
                className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-slate-400 hover:text-red-500 text-sm px-5 py-3 rounded-sm transition-colors"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
          </>
        )}
      </div>
    </main>
  );
}
